import type { AgGridSearchSelectDropdownProps, Column, SelectItem, StringSelectItem } from '@talos/kyoko';
import { Text } from '@talos/kyoko';
import type { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import type { CustomCellEditorProps } from 'ag-grid-react';
import { get } from 'lodash-es';

export type StringOrNullKeys<T> = Extract<
  {
    [K in keyof T]: T[K] extends string | null ? K : never;
  }[keyof T],
  string
>;

interface BuildSearchSelectColumnParams<TRow extends object> {
  key: StringOrNullKeys<TRow>;
  headerName: string;
  items: StringSelectItem[];
  description?: string;
  testIdPrefix?: string;
  initialSortByLabel?: boolean;
  showClear?: boolean;
  defaultValue?: string;
  minWidth?: number;
  width?: number;
  valueGetter?: (data: TRow | undefined) => string | undefined;
}

export function buildSearchSelectColumn<TRow extends object>({
  key,
  headerName,
  items,
  description,
  testIdPrefix = 'search-select',
  initialSortByLabel = true,
  showClear = true,
  defaultValue = 'Select',
  minWidth = 120,
  width = 140,
  valueGetter,
}: BuildSearchSelectColumnParams<TRow>): Column<ColDef<TRow>, TRow> {
  return {
    field: key as keyof TRow & string,
    type: 'custom',
    title: headerName,
    description,
    editable: true,
    minWidth,
    width,
    cellClass: 'ag-cell-editable',
    params: {
      valueGetter: ({ data }) => {
        if (!data) {
          return '';
        }
        return valueGetter ? (valueGetter(data) ?? '') : get(data, key);
      },
      cellRenderer: ({ data, valueFormatted, context }: ICellRendererParams) => {
        if (!data) {
          return '';
        }
        return (
          <Text
            className="ag-cell-value-container"
            data-testid={`${testIdPrefix}-${key}`}
            theme={context?.current.theme}
          >
            {valueFormatted}
          </Text>
        );
      },
      cellEditor: 'searchSelectDropdown',
      suppressKeyboardEvent: () => true,
      cellEditorPopup: true,
      cellEditorParams: (params: CustomCellEditorProps) => {
        return {
          ...params,
          useSearchSelectParams: {
            items,
            getLabel: (v: StringSelectItem) => v.label,
            initialSortByLabel,
          },
          portalize: true,
          showClear,
        } satisfies AgGridSearchSelectDropdownProps<StringSelectItem>;
      },
      valueParser: params => {
        const parsedValue = params.newValue as SelectItem<string> | string | null | undefined;
        if (typeof parsedValue === 'object' && parsedValue != null && 'value' in parsedValue) {
          return parsedValue.value ?? null;
        }
        return parsedValue ?? null;
      },
      valueFormatter: (params: ValueFormatterParams) => {
        return params.value || defaultValue;
      },
    },
  };
}
